import { motion } from "framer-motion";
import { ArrowDownRight } from "lucide-react";
import { scrollToId } from "@/hooks/useLenis";
import { useLang } from "@/i18n/LanguageContext";
import { revealNow } from "@/hooks/reveal";

export const Hero = () => {
  const { t } = useLang();
  const HEADLINE = t.hero.headline;
  return (
    <section id="hero" className="relative min-h-screen w-full overflow-hidden bg-transparent">
      {/* Fullscreen background video (hero only), stars continue below */}
      <div className="absolute inset-0 z-0 pointer-events-none">
        <video
          className="h-full w-full object-cover opacity-[0.55]"
          autoPlay
          muted
          loop
          playsInline
          data-testid="hero-video"
        >
          <source src="/brand-film.mp4" type="video/mp4" />
          <source src="/brand-film.webm" type="video/webm" />
        </video>
        <div className="absolute inset-0 bg-gradient-to-b from-black/70 via-black/40 to-black" />
        <div className="absolute inset-0 spotlight-slate mix-blend-screen" />
      </div>

      {/* Top ident line — desktop only (absolute) */}
      <motion.div
        {...revealNow(0.2)}
        className="absolute top-[92px] left-6 md:left-10 right-6 md:right-10 z-10 hidden md:flex items-center justify-between"
      >
        <span className="font-mono-tech text-[11px] tracking-[0.3em] text-white/50 uppercase">
          {t.hero.identLeft}
        </span>
        <span className="font-mono-tech text-[11px] tracking-[0.3em] text-[var(--al-gold)]/80 uppercase">
          {t.hero.identRight}
        </span>
      </motion.div>

      {/* Content */}
      <div className="relative z-10 min-h-screen flex flex-col justify-end pb-20 md:pb-28 px-6 md:px-10 max-w-[1500px] mx-auto">
        {/* Ident line — mobile only (in normal flow, above the headline) */}
        <motion.div
          {...revealNow(0.2)}
          className="flex md:hidden items-center justify-between gap-3 mb-8"
          data-testid="hero-ident-mobile"
        >
          <span className="font-mono-tech text-[10px] tracking-[0.25em] text-white/50 uppercase">
            {t.hero.identLeft}
          </span>
          <span className="font-mono-tech text-[10px] tracking-[0.25em] text-[var(--al-gold)]/80 uppercase">
            {t.hero.identRight}
          </span>
        </motion.div>

        <motion.p
          {...revealNow(0.2)}
          className="font-mono-tech text-[var(--al-gold)] text-xs md:text-sm tracking-[0.35em] uppercase mb-6"
        >
          AlloLabs UK
        </motion.p>

        <h1 className="font-cinzel font-bold tracking-tight text-white leading-[1.04] text-[2.3rem] sm:text-5xl lg:text-6xl xl:text-[4.9rem] max-w-[1200px]">
          {HEADLINE.map((line, i) => (
            <motion.span key={i} {...revealNow(0.25 + i * 0.1)} className="block">
              {line}
            </motion.span>
          ))}
        </h1>

        <motion.p
          {...revealNow(0.55)}
          className="font-body text-white/65 text-sm md:text-base leading-relaxed max-w-2xl mt-8"
        >
          {t.hero.intro}
        </motion.p>

        <motion.div
          {...revealNow(0.7)}
          className="mt-10 flex flex-wrap items-center gap-4"
        >
          <button
            onClick={() => scrollToId("catalogue")}
            className="btn-gold border border-white/20 px-8 py-4 font-body text-[12px] tracking-[0.22em] uppercase text-white"
            data-testid="hero-explore-btn"
          >
            <span className="flex items-center gap-2">
              {t.hero.viewCatalogue} <ArrowDownRight className="w-4 h-4" />
            </span>
          </button>
          <button
            onClick={() => scrollToId("contact")}
            className="font-body text-[12px] tracking-[0.22em] uppercase text-white/60 hover:text-[var(--al-gold)] transition-colors duration-300 px-2 gold-underline"
            data-testid="hero-inquire-btn"
          >
            {t.hero.inquireWholesale}
          </button>
        </motion.div>
      </div>

      {/* Scroll cue */}
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1, duration: 0.6, ease: "easeOut" }}
        className="absolute bottom-6 right-6 md:right-10 z-10 hidden md:flex items-center gap-3"
      >
        <span className="font-mono-tech text-[10px] tracking-[0.3em] text-white/40 uppercase">{t.hero.scroll}</span>
        <motion.div
          animate={{ y: [0, 8, 0] }}
          transition={{ repeat: Infinity, duration: 1.8, ease: "easeInOut" }}
          className="w-[1px] h-8 bg-gradient-to-b from-[var(--al-gold)] to-transparent"
        />
      </motion.div>
    </section>
  );
};
